import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import DataTable from "react-data-table-component";
import { Slide, Fade } from "@mui/material";
import Stack from "@mui/material/Stack";
import Nav from "react-bootstrap/Nav";
import NavDropdown from "react-bootstrap/NavDropdown";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FiEdit2 } from "react-icons/fi";
import { RiDeleteBin5Line } from "react-icons/ri";
import { MdCheckCircle, MdLink } from "react-icons/md";
import { FaGithub, FaGitlab, FaBitbucket, FaMicrosoft } from "react-icons/fa";

import ListHeading from "../../../common/RDTListHeading/ListHeading";
import SubHeaderComponent from "../../../common/RDTSubHeaderComponent/SubHeaderComponent";
import CredScanBreadcrumb from "../components/CredScanBreadcrumb";
import GeneralButton from "../../../common/SaveButton/GeneralButton";
import CustomModal from "../../../common/CustomModal/CustomModal";
import ShowAlert from "../../../common/ShowAlert/ShowAlert";
import DataTableSkeleton from "../../../common/Skeletons/DataTableSkeleton";
import Images from "../../../../utils/images";
import useWindowDimensions from "../../../../utils/getHeightWidth";
import { changeTitle } from "../../../../state/slices/header";
import {
  delayTime,
  fadedelayTime,
  slideDirection,
} from "../../../../utils/transitionEffectParams";
import { showError, showSuccess } from "../../../../utils/showMessage";
import {
  getGitRepos,
  deleteGitRepo,
  testGitConnection,
} from "../../../../api/credentialScanning";
import plus from "../../../../images/plus.svg";
import FirstPageIcon from "../../../../images/first_page.svg";
import LastPageIcon from "../../../../images/last_page.svg";
import NextPageIcon from "../../../../images/arrow_right.svg";
import PreviousPageIcon from "../../../../images/arrow_left.svg";
import "../index.scss";

const PROVIDER_LABELS = {
  github:       "GitHub",
  gitlab:       "GitLab",
  bitbucket:    "Bitbucket",
  azure_devops: "Azure DevOps",
};

const PROVIDER_COLORS = {
  github:       "#24292e",
  gitlab:       "#fc6d26",
  bitbucket:    "#0052cc",
  azure_devops: "#0078d4",
};

const PROVIDER_ICONS = {
  github:       <FaGithub    size={13} />,
  gitlab:       <FaGitlab    size={13} />,
  bitbucket:    <FaBitbucket size={13} />,
  azure_devops: <FaMicrosoft size={13} />,
};

const GitConnections = ({ scanConfigId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { height } = useWindowDimensions();

  const [repos, setRepos] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slideChecked, setSlideChecked] = useState(false);
  const [testStatus, setTestStatus] = useState({});
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [toBeDeleted, setToBeDeleted] = useState(null);
  const [colName, setColName] = useState(null);
  const [search, setSearch] = useState("");

  const tableHeight = height - 220 + "px";

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await getGitRepos(scanConfigId);
    if (data) {
      const list = Array.isArray(data) ? data : [];
      setRepos(list);
      setFilteredData(list);
    }
    setLoading(false);
    setSlideChecked(true);
  }, [scanConfigId]);

  useEffect(() => {
    dispatch(changeTitle("Discovery"));
    document.title = t("credentialScanning.gitConnections");
    load();
  }, [load, dispatch, t]);

  const handleSearch = useCallback(
    (e) => {
      if (e) e.preventDefault();
      if (!search) {
        setFilteredData(repos);
      } else {
        const q = search.toLowerCase();
        setFilteredData(
          repos.filter(
            (r) =>
              r.name?.toLowerCase().includes(q) ||
              r.organization?.toLowerCase().includes(q) ||
              r.provider?.toLowerCase().includes(q)
          )
        );
      }
    },
    [search, repos]
  );

  useEffect(() => {
    handleSearch();
  }, [search, repos, handleSearch]);

  const handleDeleteConfirm = (row) => {
    setToBeDeleted(row);
    setColName([row.name]);
    setOpenDeleteModal(true);
  };

  const handleDeleteAction = async () => {
    if (!toBeDeleted) return;
    const { error } = await deleteGitRepo(toBeDeleted.id);
    setOpenDeleteModal(false);
    setColName(null);
    setToBeDeleted(null);
    if (error) {
      showError(t("credentialScanning.toastFailedToDeleteGitConn"));
    } else {
      showSuccess(t("credentialScanning.toastGitConnDeleted"));
      load();
    }
  };

  const handleTest = async (id) => {
    setTestStatus((prev) => ({ ...prev, [id]: "testing" }));
    const { data, error } = await testGitConnection(id);
    if (data?.success) {
      setTestStatus((prev) => ({ ...prev, [id]: "success" }));
      return;
    }
    const message = data?.message || error?.message || error || t("credentialScanning.connectionFailedShort");
    showError(message);
    setTestStatus((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const columns = useMemo(
    () => [
      {
        name: t("credentialScanning.gitColProvider"),
        selector: (row) => row.provider,
        width: "145px",
        cell: (row) => (
          <span className={`cred-scan-provider-badge cred-scan-provider-badge--${row.provider}`}>
            {PROVIDER_ICONS[row.provider]}
            {PROVIDER_LABELS[row.provider] || row.provider}
          </span>
        ),
      },
      {
        name: t("credentialScanning.gitColName"),
        selector: (row) => row.name,
        grow: 1,
        sortable: true,
        cell: (row) => <span className="fw-semibold">{row.name}</span>,
      },
      {
        name: t("credentialScanning.gitColOrganization"),
        selector: (row) => row.organization,
        grow: 1,
        cell: (row) => <span className="small text-muted">{row.organization || "—"}</span>,
      },
      {
        name: t("credentialScanning.gitColToken"),
        width: "100px",
        cell: (row) => (
          <span style={{
            fontSize: 12,
            background: row.has_token ? "#e8f5e9" : "#ffebee",
            color: row.has_token ? "#2e7d32" : "#c62828",
            padding: "3px 10px", borderRadius: 4, fontWeight: 600,
          }}>
            {row.has_token ? t("credentialScanning.gitTokenStored") : t("credentialScanning.gitTokenMissing")}
          </span>
        ),
      },
      {
        name: t("credentialScanning.gitColCommitHistory"),
        minWidth: "160px",
        cell: (row) => (
          <span className="small">
            {row.scan_commit_history
              ? t("credentialScanning.gitCommitLastN", { count: row.commit_history_depth })
              : t("credentialScanning.gitCommitCurrentOnly")}
          </span>
        ),
      },
      {
        name: t("credentialScanning.gitColConnection"),
        width: "140px",
        cell: (row) => {
          const status = testStatus[row.id];
          if (status === "testing") {
            return <span className="small text-muted">{t("credentialScanning.gitTesting")}</span>;
          }
          if (status === "success") {
            return (
              <span style={{ fontSize: 12, color: "#2e7d32", display: "flex", alignItems: "center", gap: 4 }}>
                <MdCheckCircle size={13} /> {t("credentialScanning.gitConnected")}
              </span>
            );
          }
          return (
            <button
              onClick={() => handleTest(row.id)}
              style={{
                padding: "4px 10px", background: "#5177ff", color: "#fff",
                border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12,
                display: "flex", alignItems: "center", gap: 4,
              }}
            >
              <MdLink size={12} /> {t("credentialScanning.gitTest")}
            </button>
          );
        },
      },
      {
        name: t("credentialScanning.colHdrAction"),
        minWidth: "120px",
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        cell: (row) => (
          <Nav>
            <NavDropdown
              id={`git-conn-action-${row.id}`}
              title={<BsThreeDotsVertical className="text-black" />}
              menuVariant="primary"
            >
              <NavDropdown.Item
                onClick={() =>
                  navigate(
                    `/dashboard/discovery/hardcoded-credentials/git-repos/${row.id}/edit`,
                    { state: { repo: row, scanConfigId } }
                  )
                }
              >
                <FiEdit2 className="mx-2" size={13} />
                {t("credentialScanning.menuEdit")}
              </NavDropdown.Item>
              <NavDropdown.Item onClick={() => handleDeleteConfirm(row)}>
                <RiDeleteBin5Line className="mx-2" size={13} />
                {t("credentialScanning.menuDelete")}
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>
        ),
      },
    ],
    [testStatus, navigate, scanConfigId, t] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <>
      <CredScanBreadcrumb current={t("credentialScanning.gitConnections")} />
      {loading && repos.length === 0 ? (
        <DataTableSkeleton rowCount={5} columnCount={6} />
      ) : (
        <Slide timeout={delayTime} direction={slideDirection} in={slideChecked}>
          <Fade timeout={fadedelayTime} in={slideChecked}>
            <div className="main_content_container p-3 mx-auto w-100">
              <div className="position-relative bg-white" style={{ height: tableHeight }}>

                <DataTable
                  title={
                    <ListHeading
                      dataTableHeadingText={t("credentialScanning.gitConnections")}
                      dataTableHeadingIcon={Images.NetworkAnalyzer}
                    />
                  }
                  actions={
                    <Stack direction="row" spacing={2} className="py-3">
                      <GeneralButton
                        onClickEvent={() =>
                          navigate(
                            "/dashboard/discovery/hardcoded-credentials/git-repos/new",
                            { state: { scanConfigId } }
                          )
                        }
                        variant="outlined"
                        startIcon={<LazyLoadImage src={plus} alt="icon" id="center-blueShadow" />}
                        value={t("credentialScanning.btnAddConnection")}
                        size="large"
                      />
                    </Stack>
                  }
                  columns={columns}
                  data={filteredData}
                  className="rdt_container"
                  progressPending={loading}
                  progressComponent={
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
                      <div className="la-line-scale-pulse-out-rapid la-3x" style={{ color: "#5177ff" }}>
                        <div className="rounded" /><div className="rounded" /><div className="rounded" />
                        <div className="rounded" /><div className="rounded" />
                      </div>
                    </div>
                  }
                  noDataComponent={t("credentialScanning.noGitConnectionsFound")}
                  fixedHeader
                  pagination
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[5, 10, 25]}
                  highlightOnHover
                  subHeader
                  subHeaderComponent={
                    <SubHeaderComponent
                      search={search}
                      setSearch={setSearch}
                      handleSearch={handleSearch}
                      placeholder={t("credentialScanning.gitConnections")}
                      filterBy=""
                      setFilterBy={() => {}}
                      filterOptions={[]}
                      actionOptions={null}
                      selectedRows={[]}
                    />
                  }
                  subHeaderAlign="center"
                  paginationIconFirstPage={<LazyLoadImage src={FirstPageIcon} alt="first page" />}
                  paginationIconLastPage={<LazyLoadImage src={LastPageIcon} alt="last page" />}
                  paginationIconNext={<LazyLoadImage src={NextPageIcon} alt="next" />}
                  paginationIconPrevious={<LazyLoadImage src={PreviousPageIcon} alt="previous" />}
                />

                {colName && (
                  <CustomModal
                    open={openDeleteModal}
                    handleClose={() => { setOpenDeleteModal(false); setColName(null); setToBeDeleted(null); }}
                  >
                    <ShowAlert
                      setOpenAlert={setOpenDeleteModal}
                      handleAlertAction={handleDeleteAction}
                      colName={colName}
                      alertMsg={t("credentialScanning.deleteGitConnAlertMsg")}
                      headingMsg={t("credentialScanning.deleteGitConnHeading")}
                    />
                  </CustomModal>
                )}
              </div>
            </div>
          </Fade>
        </Slide>
      )}
    </>
  );
};

export default GitConnections;
